// BEGIN FILE: src/utils/houseTemporalScores.ts
/**
 * Shared house-level temporal scoring utilities.
 * Extracted from reportDataBuilder.ts so that App.tsx (and any component)
 * can compute per-house Dasha and Transit impact without importing from the
 * report module (which would create circular dependencies).
 */

// ── Parashari aspect offsets (house-based, 1-indexed) ──────────────────────
const PARASHARI_OFFSETS: Record<string, number[]> = {
  Sun:     [7],
  Moon:    [7],
  Mercury: [7],
  Venus:   [7],
  Mars:    [4, 7, 8],
  Jupiter: [5, 7, 9],
  Saturn:  [3, 7, 10],
  Rahu:    [5, 7, 9],
  Ketu:    [5, 7, 9],
};

/**
 * Compute Dasha Impact per house (0-100, 50 = neutral).
 *
 * Weights: MD=0.1, AD=0.3, PD=0.6, SD=1.0
 * A house is "activated" when the dasha lord rules it, occupies it, or aspects it.
 * Functional BENEFIC lords push the score above 50 (toward 100).
 * Functional MALEFIC lords push the score below 50 (toward 0).
 * Neutral lords and unactivated houses stay at 50.
 *
 * Display hint: (storedValue − 50) × 2  →  −100..+100 with 0 = neutral.
 */
export function computeDashaImpactByHouse(
  dashaContext: {
    MD: { planet: string; rules?: number[]; occupies?: number; aspects?: number[] };
    AD: { planet: string; rules?: number[]; occupies?: number; aspects?: number[] };
    PD: { planet: string; rules?: number[]; occupies?: number; aspects?: number[] };
    SD?: { planet: string; rules?: number[]; occupies?: number; aspects?: number[] } | null;
  } | null | undefined,
  functionalMap?: Record<string, string> | null,
): Record<number, number> {
  const result: Record<number, number> = {};
  for (let h = 1; h <= 12; h++) result[h] = 50; // neutral baseline
  if (!dashaContext) return result;

  type DashaLevel = { planet: string; rules?: number[]; occupies?: number; aspects?: number[] };
  const levels: Array<{ data: DashaLevel; weight: number }> = [
    { data: dashaContext.MD, weight: 0.1 },
    { data: dashaContext.AD, weight: 0.3 },
    { data: dashaContext.PD, weight: 0.6 },
    ...(dashaContext.SD ? [{ data: dashaContext.SD as DashaLevel, weight: 1.0 }] : []),
  ];
  const maxWeight = levels.reduce((s, l) => s + l.weight, 0);

  for (let h = 1; h <= 12; h++) {
    let beneficScore = 0;
    let maleficScore = 0;
    for (const { data, weight } of levels) {
      if (!data) continue;
      const activated =
        (Array.isArray(data.rules) && data.rules.includes(h)) ||
        data.occupies === h ||
        (Array.isArray(data.aspects) && data.aspects.includes(h));
      if (!activated) continue;
      const role = functionalMap?.[data.planet] ?? 'neutral';
      if (role === 'benefic' || role === 'yoga_karaka') beneficScore += weight;
      else if (role === 'malefic') maleficScore += weight;
      // neutral lords: no contribution (house stays at baseline 50)
    }
    const net = (beneficScore - maleficScore) / maxWeight;
    result[h] = Math.round(Math.max(0, Math.min(100, 50 + net * 50)));
  }
  return result;
}

// ── Natural friendship / enmity tables (Parashari) ───────────────────────
const NAT_FRIENDS: Record<string, string[]> = {
  Sun:     ['Moon', 'Mars', 'Jupiter'],
  Moon:    ['Sun', 'Mercury'],
  Mars:    ['Sun', 'Moon', 'Jupiter'],
  Mercury: ['Sun', 'Venus'],
  Jupiter: ['Sun', 'Moon', 'Mars'],
  Venus:   ['Mercury', 'Saturn'],
  Saturn:  ['Mercury', 'Venus'],
  Rahu:    ['Saturn', 'Venus'],
  Ketu:    ['Mars', 'Jupiter'],
};

const NAT_ENEMIES: Record<string, string[]> = {
  Sun:     ['Venus', 'Saturn'],
  Moon:    [],
  Mars:    ['Mercury'],
  Mercury: ['Moon'],
  Jupiter: ['Mercury', 'Venus'],
  Venus:   ['Sun', 'Moon'],
  Saturn:  ['Sun', 'Moon', 'Mars'],
  Rahu:    ['Sun', 'Moon'],
  Ketu:    ['Moon', 'Venus'],
};

// Sign (1-indexed) → its ruling planet
const SIGN_LORDS_H: Record<number, string> = {
  1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
  7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter',
};

// Transit dignity → effective strength multiplier
const DIGNITY_MULT: Record<string, number> = {
  exalted: 1.5, moolatrikona: 1.25, own: 1.2,
  friend:  1.1, neutral: 0.9, enemy: 0.65, debilitated: 0.5,
};

/**
 * Compute Transit Impact per house (0-100, 50 = neutral).
 *
 * Benefic transits conjuncting or aspecting a house push the score above 50;
 * malefic transits push it below 50. Neutral transits are ignored.
 *
 * Enhanced scoring multipliers (all optional — old 3-arg callers unchanged):
 *   • Transit dignity      — exalted ×1.5 … debilitated ×0.5
 *   • Sign-lord friendship — friend ×1.15, enemy ×0.85
 *   • Combustion           — combust planet ×0.6
 *   • Retrograde (vakri)   — non-node retrograde ×1.5 (stronger, more persistent)
 *
 * Display hint: (storedValue − 50) × 2  →  −100..+100 with 0 = neutral.
 */
export function computeTransitImpactByHouse(
  transitPositions: Array<{ planet: string; house: number }> | null | undefined,
  functionalMap?: Record<string, string> | null,
  blendedPlanetPct?: Partial<Record<string, number>> | null,
  transitDignities?: Partial<Record<string, string>> | null,
  ascLagnaSignIndex?: number | null,
  combustMap?: Partial<Record<string, boolean>> | null,
  retrogradeMap?: Partial<Record<string, boolean>> | null,
): Record<number, number> {
  const result: Record<number, number> = {};
  for (let h = 1; h <= 12; h++) result[h] = 50; // neutral baseline
  if (!transitPositions || transitPositions.length === 0) return result;

  const houseMap: Record<number, { b: number; m: number }> = {};
  for (let h = 1; h <= 12; h++) houseMap[h] = { b: 0, m: 0 };

  for (const tp of transitPositions) {
    const role = functionalMap?.[tp.planet] ?? 'neutral';
    if (role === 'neutral') continue; // neutral transits don't shift score

    const offsets = PARASHARI_OFFSETS[tp.planet] ?? [7];
    const influenced = new Set<number>([tp.house]);
    offsets.forEach(off => influenced.add(((tp.house - 1 + (off - 1)) % 12) + 1));

    const base = Math.abs(blendedPlanetPct?.[tp.planet] ?? 50) / 100; // 0-1

    // Dignity multiplier (falls back to 1.0 when param absent)
    const dignity = transitDignities?.[tp.planet];
    const dignityMult = dignity ? (DIGNITY_MULT[dignity] ?? 1.0) : 1.0;

    // Combustion multiplier
    const combustMult = combustMap?.[tp.planet] ? 0.6 : 1.0;

    // Retrograde multiplier — excluded for Rahu/Ketu (always retrograde; permanent bonus distorts)
    const isNode = tp.planet === 'Rahu' || tp.planet === 'Ketu';
    const retroMult = (!isNode && retrogradeMap?.[tp.planet]) ? 1.5 : 1.0;

    influenced.forEach(targetHouse => {
      // Sign-lord friendship multiplier per target house
      let friendMult = 1.0;
      if (ascLagnaSignIndex) {
        const signIndex = ((ascLagnaSignIndex - 1 + targetHouse - 1) % 12) + 1;
        const signLord = SIGN_LORDS_H[signIndex];
        if (NAT_FRIENDS[tp.planet]?.includes(signLord)) friendMult = 1.15;
        else if (NAT_ENEMIES[tp.planet]?.includes(signLord)) friendMult = 0.85;
      }

      const effectiveWt = base * dignityMult * friendMult * combustMult * retroMult;
      if (role === 'benefic' || role === 'yoga_karaka') houseMap[targetHouse].b += effectiveWt;
      else if (role === 'malefic') houseMap[targetHouse].m += effectiveWt;
    });
  }

  // Raised from 4 to 6 to preserve 0-100 scale with stacked multipliers
  const maxPossible = 6;
  for (let h = 1; h <= 12; h++) {
    const { b, m } = houseMap[h];
    const net = (b - m) / maxPossible;
    result[h] = Math.round(Math.max(0, Math.min(100, 50 + net * 50)));
  }
  return result;
}

/**
 * Compute the 4-metric combined score for a single house (0-100).
 * Weighting: 65% natal (Bhava Bala + normalized Planet Impact) + 35% temporal (Dasha + Transit)
 */
export function computeHouseCombinedScore(
  bhavaBala: number | null | undefined,
  planetImpact: number,       // -50..+50
  dashaImpact: number,        // 0-100 (50=neutral)
  transitImpact: number,      // 0-100 (50=neutral)
): number {
  const normalizedPlanetImpact = ((planetImpact + 50) / 100) * 100; // -50..+50 → 0-100
  const natalComponent = bhavaBala != null
    ? (bhavaBala + normalizedPlanetImpact) / 2
    : normalizedPlanetImpact;
  const temporalComponent = (dashaImpact + transitImpact) / 2;
  return Math.round(Math.max(0, Math.min(100, natalComponent * 0.65 + temporalComponent * 0.35)));
}

/**
 * Convert a combined score (0-100) to a 4-tier rating label.
 */
export function getHouseRating(score: number): 'Excellent' | 'Good' | 'Fair' | 'Needs Attention' {
  if (score >= 75) return 'Excellent';
  if (score >= 60) return 'Good';
  if (score >= 30) return 'Fair';
  return 'Needs Attention';
}
